import { createSelector } from "@reduxjs/toolkit";
import { IIssueApi } from "@schemas/issueSchema";
import { IZoomConfig } from "@svar-ui/react-gantt";
import { format } from "date-fns";
import { nb as nbLocale } from "date-fns/locale";
import { IRootState } from "../../../../../store";
import { isEmpty } from "../../../../utils";
import { ISSUE_STATES_OPTIONS } from "../../issueConstants";
import { selectFilteredIssuesOnBoard } from "../../issueSelectors";
import { selectHttpIssueBoard } from "../../redux/selectors/issueRtkSelectors";
import StartDateCell from "./GanttChart/StartDateCell";
import TitleCell from "./GanttChart/TitleCell";

const defaultZoomConfig: IZoomConfig = {
  maxCellWidth: 400,
  level: 3,
  levels: [
    {
      minCellWidth: 120,
      maxCellWidth: 150,
      scales: [
        { unit: "year", step: 1, format: (date: Date) => format(date, "yyyy", { locale: nbLocale }) },
        { unit: "month", step: 1, format: (date: Date) => format(date, "MMM yyyy", { locale: nbLocale }) },
      ],
    },
    {
      minCellWidth: 70,
      maxCellWidth: 150,
      scales: [
        { unit: "month", step: 1, format: (date: Date) => format(date, "MMM yyyy", { locale: nbLocale }) },
        { unit: "week", step: 1, format: (date: Date) => format(date, "'Uke' w", { locale: nbLocale }) },
      ],
    },
    {
      minCellWidth: 50,
      maxCellWidth: 50,
      scales: [
        { unit: "week", step: 1, format: (date: Date) => format(date, "'Uke' w", { locale: nbLocale }) },
        { unit: "day", step: 1, format: (date: Date) => format(date, "d", { locale: nbLocale }) },
      ],
    },
    {
      minCellWidth: 25,
      maxCellWidth: 50,
      scales: [
        {
          unit: "day",
          step: 1,
          format: (date: Date) => format(date, "MMM d", { locale: nbLocale }),
        },
      ],
    },
  ],
};

export interface ISvarGantItem {
  id: number | string; // Unique identifier for the task
  text: string; // Text description of the task
  start?: Date | null; // Start date of the task
  end?: Date | null; // End date of the task
  duration?: number | null; // Duration of the task (e.g., in days)
  progress?: number | null; // Progress percentage (0-100)
  type?: string; // Type of task (e.g., "task", "milestone")
  lazy?: boolean; // Indicates if the task is lazily loaded
  parent?: number | string; // ID of the parent task (if any)
  color?: string; // Color code for the task
  milestone?: boolean; // Optional: Indicates if the task is a milestone
}
const formatItemForSvarGantt = (item: IIssueApi): ISvarGantItem => {
  const color = ISSUE_STATES_OPTIONS.find((it) => it.value === item.status);

  const hasStart = isEmpty(item.dates?.plannedStart) === false;
  const hasEnd = isEmpty(item.dates?.due) === false;

  const rawStart = hasStart ? getGanttDate(item.dates.plannedStart as string) : undefined;
  const rawEnd = hasEnd ? getGanttDate(item.dates.due as string) : undefined;

  let start: number | undefined;
  let end: number | undefined;
  let isMilestone = false;

  // No fallback dates: only use real plannedStart / due values.
  // Treat start-only or end-only as milestones.
  if (hasStart && hasEnd) {
    // Task with both dates
    start = rawStart!;

    if (rawStart === rawEnd) {
      // Same day: represent as a 1-hour span as render hack and mark as milestone
      end = rawStart + 60 * 60 * 1000;
      isMilestone = true;
    } else {
      end = rawEnd!;
      isMilestone = false;
    }
  } else if (hasStart && !hasEnd) {
    // Milestone at the planned start date
    start = rawStart!;
    end = undefined;
    isMilestone = true;
  } else if (!hasStart && hasEnd) {
    // Milestone at the due/end date. Gantt still needs a start,
    // so place the milestone at that date as the start, with a 1-hour span hack.
    start = rawEnd!;
    end = rawEnd! + 60 * 60 * 1000;
    isMilestone = true;
  }

  return {
    id: item._id,
    text: item.title,
    start:
      start != null
        ? new Date(start)
        : end != null
        ? new Date(new Date(end).getTime() - 60 * 60 * 1000) // 1 hour before end
        : MIN_DATE,
    end: end != null ? new Date(end) : MIN_DATE,
    duration: end != null && start != null ? Math.ceil((end - start) / (1000 * 60 * 60 * 24)) : ZERO_DURATION,
    progress: undefined,
    parent: undefined,
    type: isMilestone ? "milestone" : item.status.replace(/\s+/g, "").toLowerCase(),
    color: color?.color,
    milestone: isMilestone || undefined,
    //owner: item.owner && item.owner.name,
    //assignedTo: item.assignedTo && (item.assignedTo as IUser).name,
  };
};
export const getGanttDate = (date: string): number => {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d.getTime();
};

export const getGanttSeriesForSvarGantt = (items: IIssueApi[]): ISvarGantItem[] => {
  const result: ISvarGantItem[] = [];
  const seenIds = new Set<string>();

  // 1) Add all top-level items
  for (const item of items) {
    const formatted = formatItemForSvarGantt(item);

    // If this item is a task, try to attach it to its parent via connectedIssue
    if (item.taskOrIssue === "TASK" && item.connectedIssue?.id != null) {
      const parent = items.find((_itm) => _itm.id === item.connectedIssue?.id);
      if (parent) {
        formatted.parent = parent._id;
      }
    }

    result.push(formatted);
    if (item._id != null) {
      seenIds.add(item._id.toString());
    }
  }

  // 2) Add connectedTasks as separate Gantt items (if any)
  for (const parent of items) {
    const connectedTasks = (parent.connectedTasks ?? []) as IIssueApi[];
    for (const task of connectedTasks) {
      const taskId = task._id?.toString();
      if (!taskId || seenIds.has(taskId)) continue;

      const formattedTask = formatItemForSvarGantt(task);
      formattedTask.parent = parent._id;

      result.push(formattedTask);
      seenIds.add(taskId);
    }
  }

  return result;
};

export const selectSvarGantZoomLevel = (state: IRootState) => state.issueReducerNew.svarGant.zoomLevel || "week";
export const selectSvarGantStartDate = (state: IRootState) => state.issueReducerNew.svarGant.startDate;
export const selectSvarGantEndDate = (state: IRootState) => state.issueReducerNew.svarGant.endDate;
export const selectSkipItemsWithoutEndDate = (state: IRootState) =>
  state.issueReducerNew.svarGant.skipItemsWithoutEndDate;

export const selectSvarGantZoomConfig = createSelector([selectSvarGantZoomLevel], (zoomLevel) => {
  return {
    ...defaultZoomConfig,
    level: defaultZoomConfig.levels.findIndex((level) => level.scales.some((scale) => scale.unit === zoomLevel)),
  };
});

export const selectSvarGantStart = createSelector([selectSvarGantStartDate], (startDate) => {
  return startDate ? new Date(startDate) : new Date("2025-01-01T00:00:00Z"); // Default to Jan 1, 2025
});

export const selectSvarGantEnd = createSelector([selectSvarGantEndDate], (endDate) => {
  return endDate ? new Date(endDate) : new Date("2025-12-31T23:59:59Z"); // Default to Dec 31, 2025
});

export const selectSvarGantItems = createSelector(
  [selectFilteredIssuesOnBoard, selectSkipItemsWithoutEndDate],
  (issuesOnBoard, skipItemsWithoutEndDate): ISvarGantItem[] => {
    const filteredIssuesOnBoard = skipItemsWithoutEndDate
      ? issuesOnBoard.filter((item) => item.dates?.due !== "" && item.dates?.due != null)
      : issuesOnBoard;
    return getGanttSeriesForSvarGantt(filteredIssuesOnBoard);
  },
);

export interface SvarGanttColumn {
  id: string;
  header: string;
  align?: "left" | "center" | "right";
  width?: number;
  sort?: boolean;
  template?: (value: unknown) => string;
  cell?: any;
}

const MIN_DATE = new Date(0); // 1970-01-01
const ZERO_DURATION = 0;

const formatDateForGantt = (value: unknown): string => {
  if (!(value instanceof Date)) return "";
  if (value.getTime() === 0) return ""; // hide MIN_DATE
  return value.toLocaleDateString("en-GB").replace(/\//g, ".");
};

export const selectSvarGantColumns = createSelector([selectHttpIssueBoard], (board): SvarGanttColumn[] => {
  const titleHeader = board?.issueNameSetting || "Tittel";
  return [
    { id: "text", header: titleHeader, align: "left", width: 300, cell: TitleCell },
    {
      id: "start",
      header: "startdato",
      align: "center",
      width: 90,
      cell: StartDateCell,
    },
    {
      id: "end",
      header: "sluttdato",
      align: "center",
      width: 90,
      template: formatDateForGantt,
    },
    {
      id: "duration",
      header: "Varighet",
      align: "center",
      width: 90,
      template: (v) => (v === 0 ? "" : `${v}d`),
    },
  ];
});

// Ny selector som returnerar antalet gantt-items
export const selectSvarGantItemsCount = createSelector([selectSvarGantItems], (items) => items.length);
